import dayjs from 'dayjs'
import { createSignal, createMemo, onMount, For, batch } from 'solid-js'
import { Portal } from 'solid-js/web'
import { AddOutline, CreateOutline, SearchOutline, TrashOutline, EyeOutline, CopyLine, Refresh3Line, WalletOutline, CheckmarkCircleOutline, CloseCircleOutline, NotificationsOutline, NotificationsOffOutline } from '@/icons'
import { addToast } from '@/components/Toast'
import { paginator, randomUsername, randomNickname, randomPassword } from '@lib/utils'
import { res2json } from '@/utils'
import { setStore } from '@/store'
import { playAlert, togglePlayAlert } from '@/store/play_alert'
import account from '@/store/account'

const registerSources = {
  sys: '管理员创建',
  user: '用户注册'
}

const [result, setResult] = createSignal({})

export default function Players() {
  const query = new URLSearchParams(document.location.search)
  const [keywords, setKeywords] = createSignal(query.get('k') ?? '')
  const [editUser, setEditUser] = createSignal({})
  const [selected, setSelected] = createSignal([])
  const [loading, setLoading] = createSignal(true)
  const [sending, setSending] = createSignal(false)

  const homeLink = () => {
    const query = new URLSearchParams(document.location.search)
    query.delete('lt')
    query.delete('gt')
    return `${document.location.pathname}?${query.toString()}`
  }
  const prevLink = createMemo(() => paginator.prevLink(result()))
  const nextLink = createMemo(() => paginator.nextLink(result()))
  const hasPaginator = createMemo(() => prevLink() || nextLink())

  onMount(() => {
    fetch(`/api/players?${query.toString()}`).then(res2json).then(r => {
      setResult(r.data ?? {})
    }).then(() => fetch('/api/players/count-newly').then(res2json).then(r => {
      setStore('newlyPlayerNumbers', r.data?.value ?? 0)
    })).finally(() => { setLoading(false) })
  })

  const allSelected = createMemo(() => {
    const sl = selected().length
    const rl = (result().rows ?? []).length
    return sl && sl === rl
  })
  function toggleAllSelection(val) {
    setSelected(val ? (result().rows ?? []).map(e => e.id) : [])
  }
  function toggleSelection(row) {
    setSelected(e => {
      const i = e.indexOf(row.id)
      if (i > -1) {
        e.splice(i, 1)
        return [...e]
      }
      return [...new Set([...e, row.id])]
    })
  }

  function openUserInfoDialog(user) {
    setEditUser(user)
    window.userInfo.showModal()
  }

  function openEditUserDialog(user) {
    setEditUser(user)
    window.editUser.showModal()
  }

  function openUserWalletDialog(user) {
    setEditUser(user)
    window.userWallet.showModal()
  }

  async function deleteUser(user) {
    if (confirm(`您确定要删除玩家”${user.username}[${user.nickname}]“吗?`)) {
      return fetch(`/api/players/${user.code}`, {
        method: 'DELETE'
      }).then(res2json).then(() => {
        document.location.reload()
      })
    }
  }

  async function batchOp(action) {
    setSending(true)
    fetch(`/api/players/batch-${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selected() })
    }).then(res2json).then(r => {
      document.location.reload()
    }).finally(() => { setSending(false) })
  }

  function ActionPanel() {
    return (
      <>
        {!selected().length ? (<></>) : (
          <div class="sticky bottom-2 flex items-center gap-4">
            <button type="button" onClick={() => batchOp('enable')} class="btn" disabled={sending()}>启用</button>
            <button type="button" onClick={() => batchOp('disable')} class="btn" disabled={sending()}>禁用</button>
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>玩家列表</li>
          </ul>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <h2 class="text-xl">玩家列表</h2>
            {playAlert() ? (<span role="button" onClick={() => togglePlayAlert()} class="w-5"><NotificationsOutline /></span>) : (<span role="button" onClick={() => togglePlayAlert()} class="w-5"><NotificationsOffOutline /></span>)}
          </div>
          <button type="button" onClick={() => { window.createUser.showModal() }} class="btn btn-success text-white"><span class="w-5"><AddOutline /></span>创建玩家</button>
        </div>
        <form class="join">
          <input type="text" name="k" value={keywords()} onChange={e => setKeywords(e.target.value)} class="input input-bordered join-item" placeholder="用户名或昵称" />
          <button type="submit" class="btn join-item"><span class="w-5"><SearchOutline /></span></button>
        </form>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th class="px-2 lg:px-3"><input type="checkbox" class="checkbox checkbox-sm" checked={allSelected()} onChange={e => toggleAllSelection(e.target.checked)} /></th>
                <th class="px-2 lg:px-3">状态</th>
                <th class="px-2 lg:px-3">用户名(昵称)</th>
                <th class="px-2 lg:px-3">积分</th>
                <th class="px-2 lg:px-3 text-center">玩家链接</th>
                <th class="px-2 lg:px-3 text-right">创建时间</th>
                <th class="px-2 lg:px-3 text-right">最近登陆时间</th>
                <th class="px-2 lg:px-3">注册方式</th>
                <th class="px-2 lg:px-3"></th>
              </tr>
            </thead>
            <tbody>
              <For each={result().rows ?? []}>
                {(row) => (
                  <tr class="hover:bg-base-200">
                    <td class="px-2 lg:px-3"><input type="checkbox" class="checkbox checkbox-sm" checked={selected().indexOf(row.id) > -1} onChange={e => { toggleSelection(row) }} /></td>
                    <td class={row.is_disabled ? 'px-2 lg:px-3 text-error' : 'px-2 lg:px-3 text-success'}><div class="w-6">{row.is_disabled ? <CloseCircleOutline /> : <CheckmarkCircleOutline />}</div></td>
                    <td class="px-2 lg:px-3">
                      <div class="tooltip" data-tip="编辑">
                        <button type="button" onClick={() => openEditUserDialog(row)}><span class="text-blue-500">{row.username ?? '-'}</span><span class="text-gray-500">({row.nickname ?? '-'})</span></button>
                      </div>
                    </td>
                    <td class="px-2 lg:px-3">
                      <div class="tooltip" data-tip="上下分">
                        <button onClick={() => openUserWalletDialog(row)} class="text-blue-500">{((row.score ?? 0) / 100.).toFixed(2)}</button>
                      </div>
                    </td>
                    <td class="px-2 lg:px-3 text-center">
                      <div class="tooltip" data-tip="查看玩家自动登陆信息">
                        <button type="button" onClick={() => openUserInfoDialog(row)} class="btn btn-ghost btn-sm"><span class="w-5"><EyeOutline /></span></button>
                      </div>
                    </td>
                    <td class="px-2 lg:px-3 text-right">{row.created_at ? dayjs(row.created_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                    <td class="px-2 lg:px-3 text-right">{row.last_logged_in_at ? dayjs(row.last_logged_in_at * 1000).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                    <td class="px-2 lg:px-3">{registerSources[row.data.source] ?? row.data.source ?? '-'}</td>
                    <td class="px-2 lg:px-3">
                      <div class="flex items-center justify-end">
                        <div class="tooltip" data-tip="删除">
                          <button type="button" onClick={() => deleteUser(row)} class="btn btn-ghost btn-sm"><span class="w-5"><TrashOutline /></span></button>
                        </div>
                        {/*
                        <div class="tooltip" data-tip="上下分">
                          <button type="button" onClick={() => openUserWalletDialog(row)} class="btn btn-ghost btn-sm"><span class="w-5"><WalletOutline /></span></button>
                        </div>
                        <div class="tooltip" data-tip="编辑">
                          <button type="button" onClick={() => openEditUserDialog(row)} class="btn btn-ghost btn-sm"><span class="w-5"><CreateOutline /></span></button>
                        </div>
                        */}
                      </div>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        {hasPaginator() && (
          <div class="flex items-center justify-end join">
            <button type="button" onClick={() => document.location.href = homeLink()} class="btn join-item font-normal" disabled={!prevLink()}>首页</button>
            <button type="button" onClick={() => document.location.href = prevLink()} class="btn join-item font-normal" disabled={!prevLink()}>上一页</button>
            <button type="button" onClick={() => document.location.href = nextLink()} class="btn join-item font-normal" disabled={!nextLink()}>下一页</button>
          </div>
        )}
        <ActionPanel />
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}

        <UserInfoDialog user={editUser} />
        <CreateUserDialog />
        <EditUserDialog user={editUser} />
        <UserWalletDialog user={editUser} />
      </Portal>
    </>
  )
}

function CreateUserDialog() {
  const [username, setUsername] = createSignal('')
  const [nickname, setNickname] = createSignal('')
  const [password, setPassword] = createSignal('')

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setUsername(randomUsername())
          setNickname(randomNickname())
          setPassword(randomPassword())

          setErrors({})
        })
      }
    }).observe(window.createUser, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch('/api/players', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username().trim(),
        nickname: nickname().trim(),
        password: password(),
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.createUser.close()
  }

  return (
    <dialog id="createUser" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">创建玩家</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={username()} onChange={e => setUsername(e.target.value)} class="grow" placeholder="请输入玩家用户名" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setUsername(randomUsername())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().username && (<div class="label"><span class="label-text-alt text-error">{errors().username}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="grow" placeholder="请输入玩家昵称" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setNickname(randomNickname())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">登陆密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="请输入玩家登陆密码" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}

function EditUserDialog({ user }) {
  const [nickname, setNickname] = createSignal(user().nickname)
  const [password, setPassword] = createSignal('')
  const [isDisabled, setIsDisabled] = createSignal(user().is_disabled)
  const [resetSecret, setResetSecret] = createSignal(false)

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setNickname(user().nickname)
          setPassword('')
          setIsDisabled(user().is_disabled)
          setResetSecret(false)

          setErrors({})
        })
      }
    }).observe(window.editUser, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch(`/api/players/${user().code}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        nickname: nickname().trim(),
        password: password(),
        is_disabled: isDisabled(),
        reset_secret: resetSecret()
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.editUser.close()
  }

  return (
    <dialog id="editUser" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">编辑玩家</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{user().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={nickname()} onChange={e => setNickname(e.target.value)} class="grow" placeholder="请输入玩家昵称" required />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setNickname(randomNickname())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().nickname && (<div class="label"><span class="label-text-alt text-error">{errors().nickname}</span></div>)}
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">修改密码</span></div>
          <div class="input input-bordered flex items-center">
            <input type="text" value={password()} onChange={e => setPassword(e.target.value)} class="grow" placeholder="不修改请留空" />
            <div class="tooltip" data-tip="随机生成">
              <button type="button" onClick={() => setPassword(randomPassword())} class="btn btn-ghost btn-sm"><span class="w-6"><Refresh3Line /></span></button>
            </div>
          </div>
          {errors().password && (<div class="label"><span class="label-text-alt text-error">{errors().password}</span></div>)}
        </div>
        <div>
          <label class="label cursor-pointer inline-flex gap-2">
            <input type="checkbox" onChange={e => setIsDisabled(!e.target.checked)} class="checkbox" checked={!isDisabled()} />
            <span class="label-text">启用账号</span>
          </label>
        </div>
        <div>
          <label class="label cursor-pointer inline-flex gap-2">
            <input type="checkbox" onChange={e => setResetSecret(e.target.checked)} class="checkbox" checked={resetSecret()} />
            <span class="label-text">重置自动登陆地址</span>
          </label>
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}

function UserInfoDialog({ user }) {
  const [url, setUrl] = createSignal('')

  let elQrcode

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        setUrl(`${import.meta.env.VITE_CHAT_URL_PREFIX}/${account().game_code}/login/${user().login_secret}`)
        elQrcode.innerHTML = ''
        new window.QRCode(elQrcode, { text: url(), width: 256, height: 256, correctLevel: window.QRCode.CorrectLevel.H })
      }
    }).observe(window.userInfo, { attributes: true })
  })

  function copyToClipboard() {
    navigator.clipboard.writeText(url())
    addToast({ message: '已复制到剪贴板' })
  }

  return (
    <dialog id="userInfo" class="modal">
      <div class="modal-box flex flex-col gap-4">
        <form method="dialog">
          <button type="submit" class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
        </form>
        <h3 class="text-lg">玩家自动登陆信息</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{user().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">{user().nickname}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">网址</span></div>
          <div class="textarea textarea-bordered flex items-center gap-2">
            <div class="grow break-all select-all text-base">{url()}</div>
            <div class="tooltip" data-tip="复制">
              <button type="button" onClick={() => copyToClipboard()} class="btn btn-ghost btn-sm"><span class="w-6"><CopyLine /></span></button>
            </div>
          </div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">网址二维码</span></div>
          <div ref={elQrcode} class="textarea textarea-bordered flex justify-center"></div>
        </div>
        <div class="modal-action">
          <form method="dialog">
            <button type="submit" class="btn px-8">关闭</button>
          </form>
        </div>
      </div>
    </dialog>
  )
}

export function UserWalletDialog({ user }) {
  const [amount, setAmount] = createSignal(0)
  const [action, setAction] = createSignal('charge')
  const [idempotencyKey, setIdempotencyKey] = createSignal('')

  const [sending, setSending] = createSignal(false)
  const [errors, setErrors] = createSignal({})

  let elFocus

  onMount(() => {
    new MutationObserver((e) => {
      if (e[0].target.open) {
        batch(() => {
          setAmount(0)

          setAction('charge')
          setIdempotencyKey(crypto.randomUUID())
          setErrors({})
        })
        elFocus.focus()
      }
    }).observe(window.userWallet, { attributes: true })
  })

  async function submit(e) {
    e.preventDefault()
    if (sending()) {
      return
    }

    setSending(true)
    return fetch(`/api/${account().game_code}/players/${user().code}/${action() === 'charge' ? 'deposits' : 'withdrawals'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey(),
      },
      body: JSON.stringify({
        amount: parseInt(amount()),
      })
    }).then(res2json).then(r => {
      if (r.status >= 400) {
        setErrors(r.data)
      } else {
        document.location.reload()
      }
    }).finally(() => { setSending(false) })
  }

  function cancel() {
    window.userWallet.close()
  }

  return (
    <dialog id="userWallet" class="modal">
      <form onSubmit={submit} class="modal-box flex flex-col gap-4">
        <button type="button" onClick={() => cancel()} class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" disabled={sending()}>✕</button>
        <h3 class="text-lg">玩家上下分</h3>
        <div class="form-control">
          <div class="label"><span class="label-text">用户名</span></div>
          <div class="input input-bordered flex items-center">{user().username}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">昵称</span></div>
          <div class="input input-bordered flex items-center">{user().nickname}</div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">积分</span></div>
          <div class="input input-bordered flex items-center">{((user().score ?? 0) / 100.).toFixed(2)}</div>
        </div>
        <div class="form-control">
          <div class="flex items-center gap-4">
            <label class="label cursor-pointer gap-2">
              <input type="radio" name="action" value="charge" onChange={e => setAction(e.target.value)} class="radio" checked={action() === 'charge'} />
              <span class="label-text">上分</span>
            </label>
            <label class="label cursor-pointer gap-2">
              <input type="radio" name="action" value="withdrawal" onChange={e => setAction(e.target.value)} class="radio" checked={action() === 'withdrawal'} />
              <span class="label-text">下分</span>
            </label>
          </div>
        </div>
        <div class="form-control">
          <div class="label"><span class="label-text">{action() === 'charge' ? '上分' : '下分'}金额</span></div>
          <input ref={elFocus} type="number" name="amount" value={amount()} onChange={e => setAmount(e.target.value)} class="input input-bordered" min="0" />
          {errors().amount && (<div class="label"><span class="label-text-alt text-error">{errors().amount}</span></div>)}
        </div>
        {errors().default && (<div class="text-error">{errors().default}</div>)}
        <div class="modal-action flex gap-4">
          <button type="button" onClick={() => cancel()} class="btn px-8" disabled={sending()}>取消</button>
          <button type="submit" class="btn btn-success text-white px-8" disabled={sending()}>确定</button>
        </div>
      </form>
    </dialog>
  )
}
